import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { colors } from '../../constants/constants';
import {
  Conclusions,
  EvaluationStatus,
  saveUnsavedChanges,
  selectScrollPosition,
  selectScrollReferenceId,
  setControlItemInFocus,
  updateContacts,
  updateGroups,
} from '../../redux/reducers/assessmentReducer';
import { selectDescription, selectTitle } from '../../redux/reducers/catalogsReducer';
import {
  clearSelectedFinding,
  deleteFinding,
  selectControlFindings,
  setSelectedFinding,
} from '../../redux/reducers/findingsReducer';
import DocumentLink from '../DocumentLink';
import PersonSelector from '../PersonSelector';
import Finding from '../finding/Finding';
import FindingModal from '../finding/FindingModal';
import AddContactModal from '../organization/AddContactModal';

function EvaluationControl({ control, scrollReference }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const findingsList = useSelector((state) => selectControlFindings(state, control.id));
  const scrollReferenceId = useSelector(selectScrollReferenceId);
  const scrollPosition = useSelector(selectScrollPosition);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showFindingModal, setShowFindingModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(
    () => () => {
      dispatch(saveUnsavedChanges(control.id));
    },
    [],
  );

  const handleModalClose = () => {
    setShowPersonModal(false);
    setShowFindingModal(false);
  };

  const toggleShowDetails = () => {
    setShowDetails(!showDetails);
  };

  const handlePersonAdd = (_) => {
    setShowPersonModal(true);
  };

  const handleFindingAdd = () => {
    dispatch(clearSelectedFinding());
    setShowFindingModal(true);
  };

  const handleFindingSelect = (id) => {
    dispatch(setSelectedFinding(id));
    setShowFindingModal(true);
  };

  const handleFindingDelete = (id) => {
    dispatch(deleteFinding(id));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...control };
    newDetails[name] = value;
    if (
      newDetails.evaluationStatus === EvaluationStatus.COMPLETED &&
      name === 'conclusion' &&
      value === Conclusions.ISSUES
    ) {
      newDetails.evaluationStatus = EvaluationStatus.IN_PROGRESS;
    }
    dispatch(updateGroups([newDetails]));
  };

  const [textField, setTextField] = useState({
    notes: control.notes ?? '',
  });

  const handleTextFields = (event) => {
    const { name, value } = event.target;
    const newState = { ...textField };
    newState[name] = value;
    setTextField(newState);
  };

  const handleNewEvidence = () => {
    const newDetails = { ...control };
    const newEvidence = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    newDetails.evidenceLinks = [...newDetails.evidenceLinks, newEvidence];
    dispatch(updateGroups([newDetails]));
  };

  const handleUpdateDocument = (documentData) => {
    const newDetails = { id: control.id, evidenceLinks: [...control.evidenceLinks] };
    const index = newDetails.evidenceLinks.findIndex((x) => x.id === documentData.id);
    newDetails.evidenceLinks[index] = documentData;
    dispatch(updateGroups([newDetails]));
  };
  const handleDeleteDocument = (id) => {
    const newDetails = { ...control };
    newDetails.evidenceLinks = newDetails.evidenceLinks.filter((x) => x.id !== id);
    dispatch(updateGroups([newDetails]));
  };

  const handlePersonChange = (value, _) => {
    const newDetails = { ...control };
    newDetails.contacts = value;
    dispatch(updateContacts([newDetails]));
  };

  const handleButtonGroupSelect = (name, value) => {
    const newDetails = { ...control };
    newDetails[name] = value;
    console.log(newDetails);
    dispatch(updateGroups([newDetails]));
  };

  const title = useSelector((state) => selectTitle(state, control.id));
  const description = useSelector((state) => selectDescription(state, control.id));

  const scrollFocusRef = useRef(null);

  useEffect(() => {
    /*     This handles highlightin the control "in focus" on the sidebar. 
    Margin includes an initial offset for header + desired "sensitivity" for scrolling focus. */
    const margin = 550;
    if (scrollPosition + margin >= scrollFocusRef.current.offsetTop) {
      dispatch(setControlItemInFocus(control.id));
    }
  }, [scrollPosition, control.id]);

  return (
    <>
      {showPersonModal && <AddContactModal handleClose={handleModalClose} />}
      {showFindingModal && (
        <FindingModal handleClose={handleModalClose} controlId={control.id} description={textField.notes} />
      )}
      <Paper ref={scrollFocusRef} elevation={4} sx={{ p: 2 }}>
        <div ref={scrollReferenceId === control.id ? scrollReference : null} />
        <Stack spacing={3}>
          <Stack spacing={0}>
            <Typography id={title} variant="bold">{title}</Typography>
            {description !== '' && (
              <Card variant="controlDetails">
                <CardContent variant="controlDetailsContent">
                  <Stack spacing={1} direction="row" alignItems="center">
                    <SvgIcon>
                      <InfoIcon color="primary" />
                    </SvgIcon>
                    <Typography
                      id='toggle-details'
                      onClick={toggleShowDetails}
                      variant="body1"
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          color: colors.mainGreen,
                        },
                      }}
                    >
                      {t('framework.controls.viewDescription')}
                    </Typography>
                    <IconButton onClick={toggleShowDetails} color="primary">
                      {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>
                </CardContent>
                <Collapse in={showDetails} unmountOnExit>
                  <CardContent variant="controlDetailsContent">
                    <Typography variant="body2" sx={{ color: colors.grey, px: 1 }}>
                      {description}
                    </Typography>
                  </CardContent>
                </Collapse>
              </Card>
            )}
          </Stack>
          <TextField
            size="small"
            id={`${title}-assessment-notes`}
            name="notes"
            value={textField.notes}
            onChange={handleTextFields}
            onBlur={handleChange}
            multiline
            rows="4"
            InputLabelProps={{
              shrink: true,
            }}
            label={t('control.evaluation.notes')}
            sx={{ backgroundColor: 'white' }}
          />
          <Stack width="50%">
            <PersonSelector
              id={control.id}
              title={t('assessment.details.controlContacts')}
              handlePersonChange={handlePersonChange}
              handleAddNew={handlePersonAdd}
              contacts={control.contacts}
              fill
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{t('control.evaluation.evidenceLink')}</Typography>
              <Button id={`${title}-add-documentation`} size="small" variant="outlined" onClick={handleNewEvidence} endIcon={<AddIcon />}>
                {t('control.evaluation.evidenceAdd')}
              </Button>
            </Stack>
            {control.evidenceLinks.map((link) => (
              <DocumentLink
                key={link.documentName}
                document={link}
                handleDeleteDocument={handleDeleteDocument}
                handleUpdateDocument={handleUpdateDocument}
              />
            ))}
          </Stack>

          <Box>
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
              spacing={4}
              sx={{ width: '100%', justifyContent: 'start' }}
            >
              <Stack spacing={0.5}>
                <Typography>{t('control.evaluation.conclusion.title')}</Typography>
                <ButtonGroup
                  fullWidth
                  variant="outlined"
                  subtype="buttonGroupSelect"
                  disableElevation
                  color="secondary"
                  sx={{ height: '30px' }}
                >
                  {Conclusions.list().map((conclusion) => (
                    <Button
                      variant={control.conclusion === conclusion ? 'contained' : 'outlined'}
                      subtype="buttonGroupSelect"
                      id={`${title}-conclusion-${conclusion}`}
                      key={conclusion}
                      disabled={conclusion === Conclusions.OK && findingsList.length > 0}
                      color="secondary"
                      onClick={() => handleButtonGroupSelect('conclusion', conclusion)}
                      sx={{ minWidth: 'fit-content' }}
                    >
                      {t(`control.evaluation.conclusion.${conclusion}`)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
              <Stack spacing={0.5}>
                <Typography>{t('control.evaluation.status')}</Typography>
                <ButtonGroup
                  fullWidth
                  variant="outlined"
                  subtype="buttonGroupSelect"
                  disableElevation
                  color="secondary"
                  sx={{ height: '30px' }}
                >
                  {EvaluationStatus.list().map((status) => (
                    <Button
                      variant={control.evaluationStatus === status ? 'contained' : 'outlined'}
                      subtype="buttonGroupSelect"
                      id={`${title}-status-${status}`}
                      key={status}
                      disabled={
                        status === EvaluationStatus.COMPLETED &&
                        ((control.conclusion === Conclusions.ISSUES && findingsList.length === 0) ||
                          control.conclusion === '')
                      }
                      color="secondary"
                      onClick={() => handleButtonGroupSelect('evaluationStatus', status)}
                      sx={{ minWidth: 'fit-content' }}
                    >
                      {t(`control.evaluation.statusType.${status}`)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Stack>
            {control.conclusion === Conclusions.ISSUES && findingsList.length > 0 && (
              <Stack direction="row" alignItems="center" sx={{ marginTop: '16px' }} spacing={1}>
                <SvgIcon>
                  <HelpIcon sx={{ color: colors.grey }} />
                </SvgIcon>
                <Typography sx={{ color: colors.grey }}>{t('assessment.helperRemoveFinding')}</Typography>
              </Stack>
            )}
          </Box>

          {control.conclusion !== Conclusions.OK && (
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>{t('control.evaluation.findings')}</Typography>
                <Button id={`${title}-add-finding`} size="small" variant="outlined" endIcon={<AddIcon />} onClick={() => handleFindingAdd()}>
                  {t('control.evaluation.findingAdd')}
                </Button>
              </Stack>
              {findingsList.length > 0 ? (
                findingsList.map((finding) => (
                  <Finding
                    key={finding.id}
                    finding={finding}
                    handleSelect={handleFindingSelect}
                    handleDelete={handleFindingDelete}
                  />
                ))
              ) : control.conclusion === Conclusions.ISSUES ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <SvgIcon>
                    <PriorityHighIcon color="warning" />
                  </SvgIcon>
                  <Typography color="warning.main">{t('assessment.helperAddFinding')}</Typography>
                </Stack>
              ) : (
                <Typography>{t('control.evaluation.noFindings')}</Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    </>
  );
}
export default EvaluationControl;
